/**
 * CalendarioPage — Vista mensual de ocupación.
 *
 * Cada día muestra: # pax vendidos / capacidad disponible, con color según ocupación.
 *   - Verde: <60% ocupado (hay mucho cupo)
 *   - Ámbar: 60-90% (se está llenando)
 *   - Rojo: >90% (casi lleno o lleno)
 *   - Gris: sin salidas programadas
 *
 * Click en un día → drill-down con la lista de reservas + salidas de ese día.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box, Typography, IconButton, Paper, Chip, Button, Stack, CircularProgress,
  ToggleButton, ToggleButtonGroup, Tooltip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek';
import CalendarViewDayIcon from '@mui/icons-material/CalendarViewDay';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import PlaceIcon from '@mui/icons-material/Place';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import SlideOver from '../common/SlideOver';
import ReservaSlideOver from './ReservaSlideOver';
import EstadoChip from './shared/EstadoChip';
import SaldoDestinoChip from './shared/SaldoDestinoChip';
import VoucherSubsanarChip from './shared/VoucherSubsanarChip';
import {
  API_BASE, getAuthHeaders, getEmpresaId,
  formatFechaLarga, formatHora, formatPrecio, fmtPax, todayLocal,
} from './shared/format';
import { formatNombre } from '../../utils/formatNombre';

const DIAS_SEMANA = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

function localDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function CLIMA_ICON(c) {
  if (c === 'lluvia') return '🌧️';
  if (c === 'tormenta') return '⛈️';
  if (c === 'cancelado') return '🚫';
  return null;
}

// Paleta de colores estable por destino — el mismo nombre siempre cae en el
// mismo color. Hash simple sobre el string; con 6 colores el riesgo de
// colisión visual es bajo para los volúmenes típicos de un operador (3-6
// destinos activos).
const PALETA_DESTINOS = [
  { fg: '#0d9488', bg: '#ccfbf1' },  // teal
  { fg: '#0ea5e9', bg: '#e0f2fe' },  // sky
  { fg: '#8b5cf6', bg: '#ede9fe' },  // violet
  { fg: '#f97316', bg: '#ffedd5' },  // orange
  { fg: '#ec4899', bg: '#fce7f3' },  // pink
  { fg: '#84cc16', bg: '#ecfccb' },  // lime
];
function colorPorDestino(nombre) {
  if (!nombre) return { fg: '#64748b', bg: '#f1f5f9' };
  let h = 0;
  for (let i = 0; i < nombre.length; i++) h = ((h << 5) - h + nombre.charCodeAt(i)) | 0;
  return PALETA_DESTINOS[Math.abs(h) % PALETA_DESTINOS.length];
}

// Abreviación compacta del destino para los chips en celdas chicas (mes).
// Toma la primera letra de cada palabra significativa, max 3 caracteres.
function abreviarDestino(nombre) {
  if (!nombre) return '?';
  const palabras = String(nombre).split(/\s+/).filter(p => p.length > 1);
  if (palabras.length >= 2) {
    return palabras.slice(0, 3).map(p => p[0]).join('').toUpperCase();
  }
  return String(nombre).slice(0, 3).toUpperCase();
}

// Lunes de la semana que contiene la fecha dada (Date → Date).
function inicioDeSemana(d) {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  const dow = (r.getDay() + 6) % 7;  // 0 = lunes
  r.setDate(r.getDate() - dow);
  return r;
}

function sumarDias(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function formatRangoSemana(lunes) {
  const domingo = sumarDias(lunes, 6);
  const mismoMes = lunes.getMonth() === domingo.getMonth();
  if (mismoMes) {
    return `${lunes.getDate()} – ${domingo.getDate()} de ${MESES[lunes.getMonth()]} ${lunes.getFullYear()}`;
  }
  return `${lunes.getDate()} ${MESES[lunes.getMonth()].slice(0, 3)} – ${domingo.getDate()} ${MESES[domingo.getMonth()].slice(0, 3)} ${domingo.getFullYear()}`;
}

export default function CalendarioPage() {
  const { reservaciones, refetch } = useOutletContext();
  const [salidas, setSalidas] = useState([]);
  const [loadingSalidas, setLoadingSalidas] = useState(true);
  // Mes/año visible. Default: mes actual
  const hoy = new Date();
  const [year, setYear] = useState(hoy.getFullYear());
  const [month, setMonth] = useState(hoy.getMonth());  // 0-11
  const [diaSel, setDiaSel] = useState(null);  // YYYY-MM-DD seleccionado para slide-over
  const [detalleReservaId, setDetalleReservaId] = useState(null);

  // Vista activa: mes (default), semana o día. Cada vista tiene su propio
  // cursor de navegación para evitar mezclar estados.
  const [vista, setVista] = useState('mes');
  const [cursorSemana, setCursorSemana] = useState(() => inicioDeSemana(new Date()));
  const [cursorDia, setCursorDia] = useState(() => todayLocal());

  // Filtros multi-select. Vacío = todos visibles. Se persisten en localStorage
  // para que el operador no tenga que re-aplicar al cambiar de mes.
  const [filtroDestinos, setFiltroDestinos] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('cal_filtro_destinos') || '[]')); }
    catch { return new Set(); }
  });
  const [filtroEmbarcaciones, setFiltroEmbarcaciones] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('cal_filtro_embarcaciones') || '[]')); }
    catch { return new Set(); }
  });
  useEffect(() => {
    localStorage.setItem('cal_filtro_destinos', JSON.stringify([...filtroDestinos]));
  }, [filtroDestinos]);
  useEffect(() => {
    localStorage.setItem('cal_filtro_embarcaciones', JSON.stringify([...filtroEmbarcaciones]));
  }, [filtroEmbarcaciones]);

  // Rango de carga según la vista activa. Mes/semana cargan su ventana exacta;
  // vista día carga la semana del día (para que cambiar de día sea instantáneo).
  const rangoCarga = useMemo(() => {
    if (vista === 'semana') {
      return { desde: localDateStr(cursorSemana), hasta: localDateStr(sumarDias(cursorSemana, 6)) };
    }
    if (vista === 'dia') {
      const base = new Date(cursorDia + 'T12:00:00');
      const lun = inicioDeSemana(base);
      return { desde: localDateStr(lun), hasta: localDateStr(sumarDias(lun, 6)) };
    }
    return {
      desde: localDateStr(new Date(year, month, 1)),
      hasta: localDateStr(new Date(year, month + 1, 0)),
    };
  }, [vista, year, month, cursorSemana, cursorDia]);

  const cargar = useCallback(() => {
    setLoadingSalidas(true);
    const empresaId = getEmpresaId();
    fetch(
      `${API_BASE}/turismo/salidas?empresa_id=${empresaId}&fecha_desde=${rangoCarga.desde}&fecha_hasta=${rangoCarga.hasta}`,
      { headers: getAuthHeaders() }
    )
      .then(r => r.json())
      .then(d => setSalidas(Array.isArray(d) ? d : (d.salidas || [])))
      .catch(() => setSalidas([]))
      .finally(() => setLoadingSalidas(false));
  }, [rangoCarga.desde, rangoCarga.hasta]);

  useEffect(() => { cargar(); }, [cargar]);

  // Listas únicas para los filtros — derivadas de los datos cargados. Si la
  // salida no tiene embarcación asignada, aparece bajo "sin asignar".
  const destinosUnicos = useMemo(() => {
    const set = new Set();
    salidas.forEach(s => { if (s.destino_nombre) set.add(s.destino_nombre); });
    return [...set].sort();
  }, [salidas]);
  const embarcacionesUnicas = useMemo(() => {
    const set = new Set();
    salidas.forEach(s => {
      const asignadas = s.embarcaciones_asignadas || [];
      if (asignadas.length === 0 && !s.embarcacion_nombre) {
        set.add('Sin asignar');
      } else if (s.embarcacion_nombre) {
        set.add(s.embarcacion_nombre);
      } else {
        asignadas.forEach(e => { if (e?.nombre) set.add(e.nombre); });
      }
    });
    return [...set].sort();
  }, [salidas]);

  // Predicado de filtro — true si la salida cumple ambos sets (o están vacíos).
  const cumpleFiltro = useCallback((s) => {
    if (filtroDestinos.size > 0 && !filtroDestinos.has(s.destino_nombre)) return false;
    if (filtroEmbarcaciones.size > 0) {
      const asignadas = s.embarcaciones_asignadas || [];
      const nombres = s.embarcacion_nombre
        ? [s.embarcacion_nombre]
        : (asignadas.length ? asignadas.map(e => e?.nombre).filter(Boolean) : ['Sin asignar']);
      if (!nombres.some(n => filtroEmbarcaciones.has(n))) return false;
    }
    return true;
  }, [filtroDestinos, filtroEmbarcaciones]);

  const salidasFiltradas = useMemo(() => salidas.filter(cumpleFiltro), [salidas, cumpleFiltro]);
  // Reservas: si hay filtro de destino, solo mostrar reservas cuya salida cumple.
  const reservasFiltradas = useMemo(() => {
    if (filtroDestinos.size === 0 && filtroEmbarcaciones.size === 0) return reservaciones;
    const idsSalidas = new Set(salidasFiltradas.map(s => s.id));
    return (reservaciones || []).filter(r => idsSalidas.has(r.salida_id));
  }, [reservaciones, salidasFiltradas, filtroDestinos, filtroEmbarcaciones]);

  const toggleFiltro = (set, setter, valor) => {
    const nuevo = new Set(set);
    if (nuevo.has(valor)) nuevo.delete(valor); else nuevo.add(valor);
    setter(nuevo);
  };
  const limpiarFiltros = () => {
    setFiltroDestinos(new Set());
    setFiltroEmbarcaciones(new Set());
  };
  const hayFiltrosActivos = filtroDestinos.size > 0 || filtroEmbarcaciones.size > 0;

  // Agrupar salidas y reservaciones por día (usando las listas YA filtradas).
  const datosPorDia = useMemo(() => {
    const map = {};

    salidasFiltradas.forEach(s => {
      if (!s.fecha) return;
      const dia = s.fecha.slice(0, 10);
      if (!map[dia]) map[dia] = { salidas: [], reservas: [], capacidad: 0, pax: 0, climaAlerta: false, suspendida: false, revenue: 0 };
      map[dia].salidas.push(s);
      const cap = s.capacidad_total || s.capacidad_override || 0;
      map[dia].capacidad += cap;
      map[dia].pax += s.asientos_reservados || 0;
      if (s.estado_clima && s.estado_clima !== 'normal') map[dia].climaAlerta = true;
      if (s.estado === 'suspendida' || s.estado === 'cancelada') map[dia].suspendida = true;
    });

    reservasFiltradas.forEach(r => {
      if (!r.salida_fecha) return;
      const dia = r.salida_fecha.slice(0, 10);
      if (!map[dia]) map[dia] = { salidas: [], reservas: [], capacidad: 0, pax: 0, climaAlerta: false, suspendida: false, revenue: 0 };
      map[dia].reservas.push(r);
      map[dia].revenue += Number(r.precio_total || 0);
    });

    return map;
  }, [salidasFiltradas, reservasFiltradas]);

  // Construir grilla del mes
  const grilla = useMemo(() => {
    const primerDia = new Date(year, month, 1);
    const ultimoDia = new Date(year, month + 1, 0);
    // Día de la semana del primer día (0=domingo en JS). Convertir a 0=lunes
    const offsetInicio = (primerDia.getDay() + 6) % 7;
    const totalDias = ultimoDia.getDate();
    const celdas = [];
    // Padding inicial (mes anterior, gris)
    for (let i = 0; i < offsetInicio; i++) {
      const d = new Date(year, month, -offsetInicio + i + 1);
      celdas.push({ fecha: localDateStr(d), mismoMes: false, dia: d.getDate() });
    }
    // Días del mes
    for (let d = 1; d <= totalDias; d++) {
      const f = new Date(year, month, d);
      celdas.push({ fecha: localDateStr(f), mismoMes: true, dia: d });
    }
    // Padding final hasta completar 42 (6 semanas)
    while (celdas.length < 42) {
      const ultimaCelda = celdas[celdas.length - 1];
      const d = new Date(ultimaCelda.fecha + 'T00:00:00');
      d.setDate(d.getDate() + 1);
      celdas.push({ fecha: localDateStr(d), mismoMes: false, dia: d.getDate() });
    }
    return celdas;
  }, [year, month]);

  const totalesMes = useMemo(() => {
    let totalCap = 0, totalPax = 0, totalReservas = 0, totalRevenue = 0;
    Object.values(datosPorDia).forEach(d => {
      totalCap += d.capacidad;
      totalPax += d.pax;
      totalReservas += d.reservas.length;
      totalRevenue += d.revenue || 0;
    });
    return { capacidad: totalCap, pax: totalPax, reservas: totalReservas, revenue: totalRevenue };
  }, [datosPorDia]);

  // Navegación adaptada a la vista activa.
  const navegar = (delta) => {
    if (vista === 'mes') {
      let nuevoMes = month + delta;
      let nuevoYear = year;
      if (nuevoMes < 0) { nuevoMes = 11; nuevoYear--; }
      else if (nuevoMes > 11) { nuevoMes = 0; nuevoYear++; }
      setYear(nuevoYear);
      setMonth(nuevoMes);
    } else if (vista === 'semana') {
      setCursorSemana(sumarDias(cursorSemana, delta * 7));
    } else if (vista === 'dia') {
      const base = new Date(cursorDia + 'T12:00:00');
      setCursorDia(localDateStr(sumarDias(base, delta)));
    }
  };
  const volverHoy = () => {
    setYear(hoy.getFullYear());
    setMonth(hoy.getMonth());
    setCursorSemana(inicioDeSemana(new Date()));
    setCursorDia(todayLocal());
  };

  // Etiqueta del header según vista
  const etiquetaPeriodo = vista === 'mes' ? `${MESES[month]} ${year}`
    : vista === 'semana' ? formatRangoSemana(cursorSemana)
    : formatFechaLarga(cursorDia + 'T12:00:00');

  return (
    <Box>
      {/* Header navegación + selector de vista */}
      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Calendario</Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>
            {vista === 'mes' && 'Ocupación mensual por día. Click en un día para ver detalle.'}
            {vista === 'semana' && 'Semana al detalle — salidas desglosadas por día.'}
            {vista === 'dia' && 'Día al detalle — todas las salidas y reservas del día.'}
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup
          size="small" exclusive value={vista}
          onChange={(_, v) => v && setVista(v)}
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none', fontSize: 12, fontWeight: 700,
              px: 1.5, py: 0.5, borderColor: '#e2e8f0', color: '#64748b',
            },
            '& .Mui-selected': { bgcolor: '#0d9488 !important', color: '#fff !important' },
          }}>
          <ToggleButton value="dia"><CalendarViewDayIcon sx={{ fontSize: 16, mr: 0.5 }} />Día</ToggleButton>
          <ToggleButton value="semana"><CalendarViewWeekIcon sx={{ fontSize: 16, mr: 0.5 }} />Semana</ToggleButton>
          <ToggleButton value="mes"><CalendarViewMonthIcon sx={{ fontSize: 16, mr: 0.5 }} />Mes</ToggleButton>
        </ToggleButtonGroup>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button size="small" startIcon={<CalendarTodayIcon />} onClick={volverHoy}
            sx={{ textTransform: 'none', color: '#0d9488', fontWeight: 700 }}>
            Hoy
          </Button>
          <IconButton size="small" onClick={() => navegar(-1)} sx={{ bgcolor: '#f1f5f9' }}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography sx={{ fontSize: 14, fontWeight: 700, minWidth: { xs: 140, sm: 200 }, textAlign: 'center', color: '#0d9488' }}>
            {etiquetaPeriodo}
          </Typography>
          <IconButton size="small" onClick={() => navegar(1)} sx={{ bgcolor: '#f1f5f9' }}>
            <ChevronRightIcon />
          </IconButton>
        </Stack>
      </Box>

      {/* Barra de filtros — chips multi-select por destino y embarcación. Solo se
          muestra si hay destinos cargados (evita ocupar espacio si está vacío). */}
      {(destinosUnicos.length > 0 || embarcacionesUnicas.length > 0) && (
        <Box sx={{
          mb: 1.5, p: 1, borderRadius: 1.5, bgcolor: '#f8fafc',
          border: '1px solid #e2e8f0',
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75,
        }}>
          {destinosUnicos.length > 0 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PlaceIcon sx={{ fontSize: 14, color: '#64748b' }} />
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                  Destino
                </Typography>
              </Box>
              {destinosUnicos.map(d => {
                const activo = filtroDestinos.has(d);
                const col = colorPorDestino(d);
                return (
                  <Chip key={d} size="small" label={d}
                    onClick={() => toggleFiltro(filtroDestinos, setFiltroDestinos, d)}
                    sx={{
                      fontSize: 10, fontWeight: 700, height: 22,
                      cursor: 'pointer',
                      bgcolor: activo ? col.fg : col.bg,
                      color: activo ? '#fff' : col.fg,
                      border: `1px solid ${col.fg}`,
                      '&:hover': { bgcolor: activo ? col.fg : col.bg, opacity: 0.85 },
                    }} />
                );
              })}
            </>
          )}
          {embarcacionesUnicas.length > 0 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                <DirectionsBoatIcon sx={{ fontSize: 14, color: '#64748b' }} />
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                  Embarcación
                </Typography>
              </Box>
              {embarcacionesUnicas.map(e => {
                const activo = filtroEmbarcaciones.has(e);
                return (
                  <Chip key={e} size="small" label={e}
                    onClick={() => toggleFiltro(filtroEmbarcaciones, setFiltroEmbarcaciones, e)}
                    sx={{
                      fontSize: 10, fontWeight: 700, height: 22,
                      cursor: 'pointer',
                      bgcolor: activo ? '#0d9488' : '#fff',
                      color: activo ? '#fff' : '#475569',
                      border: '1px solid #cbd5e1',
                      '&:hover': { bgcolor: activo ? '#0d9488' : '#f1f5f9' },
                    }} />
                );
              })}
            </>
          )}
          {hayFiltrosActivos && (
            <Button size="small" startIcon={<FilterAltOffIcon sx={{ fontSize: 14 }} />}
              onClick={limpiarFiltros}
              sx={{ ml: 'auto', textTransform: 'none', fontSize: 11, color: '#dc2626' }}>
              Limpiar filtros
            </Button>
          )}
        </Box>
      )}

      {/* KPIs del período visible (cambia su nombre según vista) */}
      <Box sx={{
        display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
        gap: 1.5, mb: 2,
      }}>
        <KpiCard label={`Reservas · ${vista}`} value={totalesMes.reservas} color="#0d9488" />
        <KpiCard label="Pax vendidos" value={totalesMes.pax} color="#0ea5e9" />
        <KpiCard label="Ocupación" value={totalesMes.capacidad > 0 ? `${Math.round((totalesMes.pax / totalesMes.capacidad) * 100)}%` : '—'} color="#8b5cf6"
          sub={`${totalesMes.pax} / ${totalesMes.capacidad}`} />
        <KpiCard label="Revenue" value={totalesMes.revenue > 0 ? formatPrecio(totalesMes.revenue, 'COP') : '—'} color="#f59e0b" />
      </Box>

      {loadingSalidas ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress sx={{ color: '#0d9488' }} />
        </Box>
      ) : (
        <>
          {/* ========================== VISTA MES ========================== */}
          {vista === 'mes' && (
            <>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 0.5 }}>
                {DIAS_SEMANA.map(d => (
                  <Typography key={d} sx={{
                    fontSize: 11, fontWeight: 700, color: '#64748b', textAlign: 'center',
                    textTransform: 'uppercase', letterSpacing: 0.5, py: 1,
                  }}>
                    {d}
                  </Typography>
                ))}
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                {grilla.map(celda => (
                  <CeldaMes
                    key={celda.fecha}
                    celda={celda}
                    datos={datosPorDia[celda.fecha]}
                    onClick={() => datosPorDia[celda.fecha] && setDiaSel(celda.fecha)}
                  />
                ))}
              </Box>
            </>
          )}

          {/* ========================= VISTA SEMANA ======================== */}
          {vista === 'semana' && (
            <VistaSemana
              lunes={cursorSemana}
              datosPorDia={datosPorDia}
              onAbrirDia={(fecha) => setDiaSel(fecha)}
              onAbrirReserva={(id) => setDetalleReservaId(id)}
            />
          )}

          {/* ========================== VISTA DÍA ========================== */}
          {vista === 'dia' && (
            <VistaDia
              fecha={cursorDia}
              datos={datosPorDia[cursorDia]}
              onAbrirReserva={(id) => setDetalleReservaId(id)}
            />
          )}
        </>
      )}

      {/* Leyenda — solo en vista mes (las otras vistas ya son explícitas) */}
      {vista === 'mes' && (
        <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>
            Ocupación:
          </Typography>
          <LegendItem color="#16a34a" bg="#dcfce7" label="Bajo (<60%)" />
          <LegendItem color="#f59e0b" bg="#fef3c7" label="Medio (60-90%)" />
          <LegendItem color="#dc2626" bg="#fee2e2" label="Casi lleno (≥90%)" />
          <LegendItem color="#94a3b8" bg="#f8fafc" label="Sin salidas" />
        </Box>
      )}

      {/* SlideOver detalle del día */}
      {diaSel && (
        <DiaDetalleSlideOver
          fecha={diaSel}
          datos={datosPorDia[diaSel]}
          onClose={() => setDiaSel(null)}
          onOpenReserva={(id) => setDetalleReservaId(id)}
        />
      )}

      {/* SlideOver de detalle de reserva */}
      {detalleReservaId && (
        <ReservaSlideOver
          reservaId={detalleReservaId}
          onClose={() => setDetalleReservaId(null)}
          onUpdated={refetch}
        />
      )}
    </Box>
  );
}

function KpiCard({ label, value, color, sub }) {
  return (
    <Paper sx={{
      p: 1.25, borderRadius: 2,
      borderLeft: `4px solid ${color}`,
      background: 'rgba(255,255,255,0.85)',
      backdropFilter: 'blur(20px)',
    }}>
      <Typography sx={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 18, fontWeight: 800, color }}>{value}</Typography>
      {sub && <Typography sx={{ fontSize: 10, color: '#64748b' }}>{sub}</Typography>}
    </Paper>
  );
}

/**
 * TooltipDia — contenido rich del tooltip al hover sobre un día en mes/semana.
 * Muestra ocupación, lista corta de salidas con sus chips de color, y revenue
 * cuando aplica. Pensado para responder "¿qué hay este día?" sin click.
 */
function TooltipDia({ fecha, datos }) {
  if (!datos) {
    return (
      <Box sx={{ p: 0.5 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#fff' }}>
          {formatFechaLarga(fecha + 'T12:00:00')}
        </Typography>
        <Typography sx={{ fontSize: 10, color: '#cbd5e1', fontStyle: 'italic' }}>
          Sin salidas programadas.
        </Typography>
      </Box>
    );
  }
  const ocupacion = datos.capacidad > 0 ? Math.round((datos.pax / datos.capacidad) * 100) : 0;
  return (
    <Box sx={{ p: 0.5, minWidth: 200 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#fff', mb: 0.5 }}>
        {formatFechaLarga(fecha + 'T12:00:00')}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
        <Typography sx={{ fontSize: 10, color: '#cbd5e1' }}>
          <b style={{ color: '#fff' }}>{datos.pax}/{datos.capacidad}</b> pax ({ocupacion}%)
        </Typography>
        <Typography sx={{ fontSize: 10, color: '#cbd5e1' }}>
          · <b style={{ color: '#fff' }}>{datos.reservas.length}</b> reserva{datos.reservas.length !== 1 ? 's' : ''}
        </Typography>
      </Box>
      {datos.revenue > 0 && (
        <Typography sx={{ fontSize: 10, color: '#fbbf24', fontWeight: 700, mb: 0.5 }}>
          Revenue: {formatPrecio(datos.revenue, 'COP')}
        </Typography>
      )}
      {datos.salidas.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.5 }}>
          {datos.salidas.slice(0, 4).map(s => {
            const cap = s.capacidad_total || 0;
            const pax = s.asientos_reservados || 0;
            const col = colorPorDestino(s.destino_nombre);
            return (
              <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 8, height: 8, bgcolor: col.fg, borderRadius: '50%' }} />
                <Typography sx={{ fontSize: 10, color: '#fff', flex: 1 }}>
                  {formatHora(s.hora_salida)} · {s.destino_nombre || s.paquete_nombre}
                  {s.lancha_compartida && (
                    <span style={{ color: '#fbbf24', fontWeight: 700 }}> · compartida</span>
                  )}
                </Typography>
                <Typography sx={{ fontSize: 10, color: '#cbd5e1' }}>
                  {s.lancha_compartida ? s.asientos_disponibles_efectivos : pax}
                  {s.lancha_compartida ? `/${cap}` : `/${cap}`}
                </Typography>
              </Box>
            );
          })}
          {datos.salidas.length > 4 && (
            <Typography sx={{ fontSize: 9, color: '#94a3b8', fontStyle: 'italic' }}>
              + {datos.salidas.length - 4} más…
            </Typography>
          )}
        </Box>
      )}
      <Typography sx={{ fontSize: 9, color: '#94a3b8', mt: 0.75, fontStyle: 'italic' }}>
        Click para ver detalle.
      </Typography>
    </Box>
  );
}

/**
 * CeldaMes — Celda del calendario mensual con chips compactos por salida
 * (max 3 visibles, "+N" si hay más) y tooltip rico al hover.
 */
function CeldaMes({ celda, datos, onClick }) {
  const ocupacion = datos && datos.capacidad > 0 ? (datos.pax / datos.capacidad) * 100 : null;
  const colorOcupacion = ocupacion == null ? '#e2e8f0'
    : ocupacion >= 90 ? '#dc2626'
    : ocupacion >= 60 ? '#f59e0b'
    : '#16a34a';
  const bgOcupacion = ocupacion == null ? '#f8fafc'
    : ocupacion >= 90 ? '#fee2e2'
    : ocupacion >= 60 ? '#fef3c7'
    : '#dcfce7';
  const esHoy = celda.fecha === todayLocal();
  const tieneDatos = datos && (datos.salidas.length > 0 || datos.reservas.length > 0);
  const salidasVisibles = (datos?.salidas || []).slice(0, 3);
  const salidasExtra = (datos?.salidas?.length || 0) - salidasVisibles.length;
  const climaSalida = datos && datos.salidas.find(s => s.estado_clima && s.estado_clima !== 'normal');
  const climaIcon = climaSalida ? CLIMA_ICON(climaSalida.estado_clima) : null;
  const suspendida = datos && datos.suspendida;

  const cell = (
    <Paper elevation={0}
      onClick={onClick}
      sx={{
        minHeight: { xs: 64, sm: 92 },
        p: 0.75, borderRadius: 1.5,
        bgcolor: celda.mismoMes ? bgOcupacion : '#fafafa',
        border: esHoy ? `2px solid #0d9488` : '1px solid #e2e8f0',
        opacity: celda.mismoMes ? 1 : 0.4,
        cursor: tieneDatos ? 'pointer' : 'default',
        transition: 'transform 0.15s',
        '&:hover': tieneDatos ? {
          transform: 'scale(1.03)',
          boxShadow: `0 4px 12px ${colorOcupacion}40`,
        } : {},
        display: 'flex', flexDirection: 'column',
      }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Typography sx={{
          fontSize: 11, fontWeight: esHoy ? 800 : 700,
          color: esHoy ? '#0d9488' : (celda.mismoMes ? '#0f172a' : '#94a3b8'),
        }}>
          {celda.dia}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          {climaIcon && <Typography sx={{ fontSize: 10 }}>{climaIcon}</Typography>}
          {suspendida && <WarningAmberIcon sx={{ fontSize: 12, color: '#dc2626' }} />}
        </Box>
      </Box>

      {tieneDatos && celda.mismoMes && (
        <>
          {/* Chips por salida — abreviatura + ocupación */}
          {salidasVisibles.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25, mt: 0.5 }}>
              {salidasVisibles.map(s => {
                const col = colorPorDestino(s.destino_nombre);
                const pax = s.asientos_reservados || 0;
                const cap = s.capacidad_total || 0;
                return (
                  <Box key={s.id}
                    sx={{
                      px: 0.5, py: 0.1, borderRadius: 0.5,
                      bgcolor: col.fg, color: '#fff',
                      fontSize: 8.5, fontWeight: 700, lineHeight: 1.3,
                      display: { xs: 'none', sm: 'block' },
                    }}>
                    {abreviarDestino(s.destino_nombre)} {pax}/{cap}
                  </Box>
                );
              })}
              {salidasExtra > 0 && (
                <Box sx={{
                  px: 0.5, py: 0.1, borderRadius: 0.5,
                  bgcolor: '#64748b', color: '#fff',
                  fontSize: 8.5, fontWeight: 700, lineHeight: 1.3,
                  display: { xs: 'none', sm: 'block' },
                }}>
                  +{salidasExtra}
                </Box>
              )}
            </Box>
          )}

          <Box sx={{ mt: 'auto' }}>
            {datos.capacidad > 0 && (
              <Typography sx={{ fontSize: 10, fontWeight: 800, color: colorOcupacion, lineHeight: 1 }}>
                {datos.pax}/{datos.capacidad}
              </Typography>
            )}
            {datos.reservas.length > 0 && (
              <Typography sx={{ fontSize: 9, color: '#64748b', lineHeight: 1.2 }}>
                {datos.reservas.length} reserva{datos.reservas.length !== 1 ? 's' : ''}
              </Typography>
            )}
            {ocupacion != null && (
              <Box sx={{ mt: 0.25, height: 3, bgcolor: 'rgba(0,0,0,0.08)', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{
                  width: `${Math.min(ocupacion, 100)}%`, height: '100%',
                  bgcolor: colorOcupacion, transition: 'width 0.3s',
                }} />
              </Box>
            )}
          </Box>
        </>
      )}
    </Paper>
  );

  // Tooltip rich solo cuando hay datos o cuando es del mismo mes (para que
  // celdas vacías del mes anterior/siguiente no muestren tooltip molesto).
  if (!celda.mismoMes && !tieneDatos) return cell;
  return (
    <Tooltip
      title={<TooltipDia fecha={celda.fecha} datos={datos} />}
      arrow placement="top"
      enterDelay={300} leaveDelay={0}
      componentsProps={{
        tooltip: { sx: { bgcolor: '#1e293b', maxWidth: 280, p: 0.75 } },
        arrow: { sx: { color: '#1e293b' } },
      }}>
      <Box sx={{ display: 'block' }}>{cell}</Box>
    </Tooltip>
  );
}

/**
 * VistaSemana — 7 columnas (Lun-Dom). Cada columna lista las salidas del día
 * como cards apilados. El operador ve de un golpe qué destinos corren cada día
 * de la semana y cuánto cupo queda en cada salida.
 */
function VistaSemana({ lunes, datosPorDia, onAbrirDia, onAbrirReserva }) {
  const dias = Array.from({ length: 7 }, (_, i) => sumarDias(lunes, i));
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.75 }}>
      {dias.map((d, idx) => {
        const fecha = (d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`)(d);
        const datos = datosPorDia[fecha];
        const esHoy = fecha === todayLocal();
        const ocupacion = datos && datos.capacidad > 0 ? Math.round((datos.pax / datos.capacidad) * 100) : null;
        return (
          <Paper key={fecha} elevation={0}
            sx={{
              p: 1, borderRadius: 2,
              border: esHoy ? '2px solid #0d9488' : '1px solid #e2e8f0',
              bgcolor: esHoy ? 'rgba(13,148,136,0.04)' : '#fff',
              display: 'flex', flexDirection: 'column', gap: 0.5,
              minHeight: 280,
            }}>
            {/* Header del día */}
            <Box sx={{ pb: 0.75, borderBottom: '1px solid #e2e8f0', cursor: datos ? 'pointer' : 'default' }}
              onClick={() => datos && onAbrirDia(fecha)}>
              <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {DIAS_SEMANA[idx]}
              </Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 800, color: esHoy ? '#0d9488' : '#0f172a', lineHeight: 1 }}>
                {d.getDate()}
              </Typography>
              {ocupacion != null && (
                <Typography sx={{ fontSize: 9, color: '#64748b', mt: 0.25 }}>
                  {datos.pax}/{datos.capacidad} · {ocupacion}%
                </Typography>
              )}
            </Box>

            {/* Salidas del día */}
            {!datos || datos.salidas.length === 0 ? (
              <Typography sx={{ fontSize: 10, color: '#cbd5e1', fontStyle: 'italic', textAlign: 'center', mt: 2 }}>
                Sin salidas
              </Typography>
            ) : (
              <Stack spacing={0.5}>
                {datos.salidas.map(s => {
                  const col = colorPorDestino(s.destino_nombre);
                  const pax = s.asientos_reservados || 0;
                  const cap = s.capacidad_total || 0;
                  // Capacidad efectiva: si la lancha es compartida, descontar las
                  // reservas de las otras salidas que comparten el viaje.
                  const dispEfectivos = s.lancha_compartida
                    ? (s.asientos_disponibles_efectivos ?? (cap - pax))
                    : (cap - pax);
                  const pctOcup = cap > 0 ? ((cap - dispEfectivos) / cap) * 100 : 0;
                  const suspendida = s.estado === 'suspendida' || s.estado === 'cancelada';
                  const lanchasNombres = (s.embarcaciones_asignadas || [])
                    .map(e => e.embarcacion_nombre)
                    .filter(Boolean);
                  return (
                    <Paper key={s.id} variant="outlined"
                      onClick={() => onAbrirDia(fecha)}
                      sx={{
                        p: 0.75, cursor: 'pointer',
                        borderLeft: `3px solid ${col.fg}`,
                        bgcolor: suspendida ? '#fef2f2' : col.bg,
                        '&:hover': { boxShadow: `0 2px 6px ${col.fg}40` },
                      }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: col.fg, textTransform: 'uppercase' }}>
                          {formatHora(s.hora_salida)}
                        </Typography>
                        {s.estado_clima && s.estado_clima !== 'normal' && (
                          <Typography sx={{ fontSize: 9 }}>{CLIMA_ICON(s.estado_clima)}</Typography>
                        )}
                      </Box>
                      <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                        {s.destino_nombre || s.paquete_nombre}
                      </Typography>
                      {lanchasNombres.length > 0 && (
                        <Typography sx={{ fontSize: 9, color: col.fg, fontWeight: 600 }}>
                          🚤 {lanchasNombres.join(', ')}
                          {s.lancha_compartida && (
                            <span style={{ color: '#b45309', fontWeight: 700 }}> · compartida</span>
                          )}
                        </Typography>
                      )}
                      <Typography sx={{ fontSize: 9, color: '#64748b' }}>
                        {pax}/{cap} · {dispEfectivos} disp{s.lancha_compartida ? ' (real)' : ''}
                      </Typography>
                      <Box sx={{ mt: 0.25, height: 2, bgcolor: 'rgba(0,0,0,0.08)', borderRadius: 1, overflow: 'hidden' }}>
                        <Box sx={{ width: `${Math.min(pctOcup, 100)}%`, height: '100%', bgcolor: col.fg }} />
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Paper>
        );
      })}
    </Box>
  );
}

/**
 * VistaDia — todas las salidas del día en cards grandes con reservas listadas.
 * Es la vista que más usa el coordinador en muelle: cada card es una salida
 * con su lancha, ocupación y la lista de pasajeros confirmados.
 */
function VistaDia({ fecha, datos, onAbrirReserva }) {
  if (!datos || (datos.salidas.length === 0 && datos.reservas.length === 0)) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', bgcolor: '#f8fafc', border: '1px dashed #cbd5e1' }}>
        <Typography sx={{ fontSize: 14, color: '#94a3b8', fontStyle: 'italic' }}>
          No hay salidas ni reservas programadas para este día.
        </Typography>
      </Paper>
    );
  }
  // Agrupar reservas por salida_id para mostrarlas dentro de cada card.
  const reservasPorSalida = {};
  datos.reservas.forEach(r => {
    const sid = r.salida_id || 'sin_salida';
    if (!reservasPorSalida[sid]) reservasPorSalida[sid] = [];
    reservasPorSalida[sid].push(r);
  });

  return (
    <Stack spacing={1.5}>
      {datos.salidas.length === 0 ? (
        <Paper sx={{ p: 2, bgcolor: '#fef3c7', border: '1px solid #f59e0b' }}>
          <Typography sx={{ fontSize: 12, color: '#92400e' }}>
            Hay {datos.reservas.length} reserva(s) pero ninguna salida programada para este día.
          </Typography>
        </Paper>
      ) : datos.salidas.map(s => {
        const col = colorPorDestino(s.destino_nombre);
        const pax = s.asientos_reservados || 0;
        const cap = s.capacidad_total || 0;
        const libre = cap - pax;
        const pctOcup = cap > 0 ? (pax / cap) * 100 : 0;
        const reservasDeEsta = reservasPorSalida[s.id] || [];
        const suspendida = s.estado === 'suspendida' || s.estado === 'cancelada';
        const embarcacionesNombres = s.embarcacion_nombre
          ? [s.embarcacion_nombre]
          : (s.embarcaciones_asignadas || []).map(e => e?.nombre).filter(Boolean);

        return (
          <Paper key={s.id} elevation={0}
            sx={{
              borderRadius: 2, overflow: 'hidden',
              border: '1px solid #e2e8f0',
              borderLeft: `4px solid ${col.fg}`,
            }}>
            {/* Header de la salida */}
            <Box sx={{ p: 1.5, bgcolor: col.bg, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 800, color: col.fg }}>
                    {formatHora(s.hora_salida)} → {formatHora(s.hora_regreso) || '—'}
                  </Typography>
                  {suspendida && (
                    <Chip size="small" label="Suspendida" sx={{ bgcolor: '#dc2626', color: '#fff', fontSize: 9, height: 18 }} />
                  )}
                  {s.estado_clima && s.estado_clima !== 'normal' && (
                    <Chip size="small" label={`${CLIMA_ICON(s.estado_clima)} ${s.estado_clima}`}
                      sx={{ bgcolor: '#fef3c7', color: '#92400e', fontSize: 9, height: 18 }} />
                  )}
                </Box>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
                  {s.destino_nombre || s.paquete_nombre}
                </Typography>
                <Typography sx={{ fontSize: 10, color: '#475569' }}>
                  {s.paquete_nombre}
                  {s.punto_embarque_nombre && ` · ${s.punto_embarque_nombre}`}
                  {embarcacionesNombres.length > 0 && ` · 🚤 ${embarcacionesNombres.join(', ')}`}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontSize: 20, fontWeight: 800, color: col.fg, lineHeight: 1 }}>
                  {pax}/{cap}
                </Typography>
                <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                  {libre} libre{libre !== 1 ? 's' : ''} · {Math.round(pctOcup)}%
                </Typography>
                <Box sx={{ mt: 0.5, width: 80, height: 4, bgcolor: 'rgba(0,0,0,0.1)', borderRadius: 2 }}>
                  <Box sx={{ width: `${Math.min(pctOcup, 100)}%`, height: '100%', bgcolor: col.fg, borderRadius: 2 }} />
                </Box>
              </Box>
            </Box>

            {/* Reservas de esta salida */}
            {reservasDeEsta.length === 0 ? (
              <Box sx={{ p: 1.5 }}>
                <Typography sx={{ fontSize: 11, color: '#cbd5e1', fontStyle: 'italic' }}>
                  Sin reservas registradas en esta salida.
                </Typography>
              </Box>
            ) : (
              <Stack divider={<Box sx={{ height: '1px', bgcolor: '#f1f5f9' }} />}>
                {reservasDeEsta.map(r => (
                  <Box key={r.id}
                    onClick={() => onAbrirReserva(r.id)}
                    sx={{
                      p: 1, cursor: 'pointer',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap',
                      '&:hover': { bgcolor: '#f8fafc' },
                    }}>
                    <Box sx={{ flex: 1, minWidth: 180 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: 10.5, fontWeight: 700, color: '#475569' }}>
                          {r.codigo_reserva}
                        </Typography>
                        <EstadoChip estado={r.estado} saldoPendiente={r.saldo_pendiente}
                          saldoMuelleMonto={r.saldo_muelle_monto}
                          cxcAgenciaMonto={r.cxc_agencia_monto} />
                        <SaldoDestinoChip saldoPendiente={r.saldo_pendiente}
                          saldoMuelleMonto={r.saldo_muelle_monto}
                          cxcAgenciaMonto={r.cxc_agencia_monto}
                                          saldoACargoDe={r.saldo_a_cargo_de}
                                          saldoMuelleMonto={r.saldo_muelle_monto}
                                          cxcAgenciaMonto={r.cxc_agencia_monto}
                                          formaPagoAgencia={r.forma_pago_agencia} />
                        <VoucherSubsanarChip voucherPendienteSubsanar={r.voucher_pendiente_subsanar} />
                      </Box>
                      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{formatNombre(r.nombre_cliente)}</Typography>
                      <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                        {fmtPax(r)}
                        {r.agencia_nombre && ` · 🏢 ${r.agencia_nombre}`}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0d9488' }}>
                      {formatPrecio(r.precio_total, r.moneda)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        );
      })}
    </Stack>
  );
}

function LegendItem({ color, bg, label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: bg, border: `1px solid ${color}` }} />
      <Typography sx={{ fontSize: 10, color: '#475569' }}>{label}</Typography>
    </Box>
  );
}

function DiaDetalleSlideOver({ fecha, datos, onClose, onOpenReserva }) {
  if (!datos) return null;
  const ocupacion = datos.capacidad > 0 ? Math.round((datos.pax / datos.capacidad) * 100) : 0;

  return (
    <SlideOver open onClose={onClose}
      title={formatFechaLarga(fecha + 'T12:00:00')}
      subtitle={`${datos.salidas.length} salida(s) · ${datos.reservas.length} reserva(s) · ${datos.pax}/${datos.capacidad} pax (${ocupacion}%)`}
      icon={<CalendarTodayIcon />}
      footer={
        <Button onClick={onClose} startIcon={<CloseIcon />}
          sx={{ textTransform: 'none' }}>Cerrar</Button>
      }>
      <Stack spacing={2}>
        {/* Salidas del día */}
        {datos.salidas.length > 0 && (
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#0d9488', textTransform: 'uppercase', mb: 0.75 }}>
              Salidas programadas
            </Typography>
            <Stack spacing={0.75}>
              {datos.salidas.map(s => {
                const cap = s.capacidad_total || s.capacidad_override || 0;
                const ocupados = s.asientos_reservados || 0;
                const libres = cap - ocupados;
                return (
                  <Paper key={s.id} variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                      <Box sx={{ flex: 1, minWidth: 140 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                          {s.paquete_nombre || s.paquete?.nombre}
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                          {formatHora(s.hora_salida)} → {formatHora(s.hora_regreso) || '—'}
                          {s.estado === 'suspendida' && ' · ⚠️ Suspendida'}
                        </Typography>
                      </Box>
                      <Chip size="small"
                        label={`${ocupados}/${cap} · ${libres} libres`}
                        sx={{
                          fontSize: 10, fontWeight: 700,
                          bgcolor: libres === 0 ? '#fee2e2' : libres < cap * 0.3 ? '#fef3c7' : '#dcfce7',
                          color: libres === 0 ? '#991b1b' : libres < cap * 0.3 ? '#92400e' : '#166534',
                        }} />
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          </Box>
        )}

        {/* Reservas del día */}
        {datos.reservas.length > 0 ? (
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#0d9488', textTransform: 'uppercase', mb: 0.75 }}>
              Reservas ({datos.reservas.length})
            </Typography>
            <Stack spacing={0.5}>
              {datos.reservas.map(r => (
                <Paper key={r.id} variant="outlined" sx={{ p: 1, borderRadius: 1.5, cursor: 'pointer',
                  '&:hover': { borderColor: '#0d9488', boxShadow: '0 2px 6px rgba(13,148,136,0.15)' } }}
                  onClick={() => onOpenReserva(r.id)}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: 180 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700 }}>
                          {r.codigo_reserva}
                        </Typography>
                        <EstadoChip estado={r.estado} saldoPendiente={r.saldo_pendiente}
                          saldoMuelleMonto={r.saldo_muelle_monto}
                          cxcAgenciaMonto={r.cxc_agencia_monto} />
                        <SaldoDestinoChip saldoPendiente={r.saldo_pendiente}
                          saldoMuelleMonto={r.saldo_muelle_monto}
                          cxcAgenciaMonto={r.cxc_agencia_monto}
                                          saldoACargoDe={r.saldo_a_cargo_de}
                                          saldoMuelleMonto={r.saldo_muelle_monto}
                                          cxcAgenciaMonto={r.cxc_agencia_monto}
                                          formaPagoAgencia={r.forma_pago_agencia} />
                        <VoucherSubsanarChip voucherPendienteSubsanar={r.voucher_pendiente_subsanar} />
                      </Box>
                      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{formatNombre(r.nombre_cliente)}</Typography>
                      <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                        {r.paquete_nombre} · {fmtPax(r)}
                        {r.agencia_nombre && ` · 🏢 ${r.agencia_nombre}`}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0d9488' }}>
                      {formatPrecio(r.precio_total, r.moneda)}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Box>
        ) : datos.salidas.length > 0 ? (
          <Typography sx={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
            Sin reservas registradas para este día.
          </Typography>
        ) : null}
      </Stack>
    </SlideOver>
  );
}
